import { cx } from "@/lib/cx";

/**
 * UX-DR18's completeness meter (Story 5.3, judgement call 5) — built,
 * tested, and exported here, but **not placed anywhere in this story's own
 * property record page**. UX-DR18's epics.md mapping states the consumer is
 * Phase 1b's equipment story; the component belongs to the property record,
 * but there is no real equipment/checklist data in Phase 1a to drive it.
 * Rendering it now with fabricated or all-zero data would violate this
 * codebase's own "never render a placeholder for what hasn't shipped"
 * discipline (VC-5.3-14) — so this file exists purely as a ready-made,
 * import-and-place component for that later story.
 *
 * A 6px track (`h-1.5`), a words-not-percentages label — "3 of 11 items
 * still need details", never "27% complete" — and an optional `onTap`
 * callback that turns the whole meter into a real button (the "tap-to-
 * filter" affordance a checklist screen would use to jump straight to its
 * own incomplete rows).
 */
export interface CompletenessMeterProps {
  /** How many of `total` items already have every detail filled in. */
  completed: number;
  /** The whole count this meter is tracking — e.g. eleven pieces of
   * equipment across a property. */
  total: number;
  /** Present only when tapping the meter should jump to a filtered view of
   * the incomplete items. Absent renders a plain, non-interactive meter. */
  onTap?: () => void;
  className?: string;
}

function completenessLabel(completed: number, total: number): string {
  if (total <= 0) {
    return "Nothing to track yet.";
  }
  const remaining = Math.max(0, total - completed);
  if (remaining === 0) {
    return `All ${total} of ${total} items have their details.`;
  }
  return `${remaining} of ${total} items still need details`;
}

export function CompletenessMeter({ completed, total, onTap, className }: CompletenessMeterProps) {
  const clampedTotal = Math.max(0, total);
  const clampedCompleted = Math.min(Math.max(0, completed), clampedTotal);
  const fraction = clampedTotal === 0 ? 0 : clampedCompleted / clampedTotal;
  const label = completenessLabel(clampedCompleted, clampedTotal);

  const track = (
    <div
      role="progressbar"
      aria-valuenow={clampedCompleted}
      aria-valuemin={0}
      aria-valuemax={clampedTotal}
      aria-label={label}
      className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
    >
      <div
        className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out"
        style={{ width: `${Math.round(fraction * 100)}%` }}
      />
    </div>
  );

  if (onTap === undefined) {
    return (
      <div className={cx("flex flex-col gap-1.5", className)}>
        {track}
        <p className="text-meta text-ink-secondary">{label}</p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onTap}
      className={cx(
        "flex w-full flex-col gap-1.5 rounded-md text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        className,
      )}
    >
      {track}
      <p className="text-meta text-accent underline">{label}</p>
    </button>
  );
}
